import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import InboundFilter from "../../components/inbound/inboundFilter/InboundFilter";
import TableNfe from "../../components/inbound/tableNfe/TableNfe";

import { fetchNfeHeaders } from "../../services/nfeHeaderService";
import { fetchCteHeaders } from "../../services/cteHeaderService";
import { translateBackendMessage } from "../../utilities/translateBackendMessage";

const FILTER_STORAGE_KEY = "inbound-list-filters";

const InboundListPage = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith("en");
  const startOfMonth = dayjs().startOf("month").format("YYYY-MM-DD");
  const endOfMonth = dayjs().endOf("month").format("YYYY-MM-DD");

  const defaultFilters = {
    docType: "nfe",
    process: [],
    step: [],
    accessKey: "",
    issueDateFrom: "",
    issueDateTo: "",
    issueDateRange: "",
    receivedDateFrom: startOfMonth,
    receivedDateTo: endOfMonth,
    receivedDateRange: `${dayjs(startOfMonth).format("DD/MM/YYYY")} - ${dayjs(endOfMonth).format("DD/MM/YYYY")}`,
    supplier_cnpj: "",
    plant: "",
  };

  const [rowsNfe, setRowsNfe] = useState([]);
  const [rowsCte, setRowsCte] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(FILTER_STORAGE_KEY);
      return saved ? { ...defaultFilters, ...JSON.parse(saved) } : defaultFilters;
    } catch {
      return defaultFilters;
    }
  });

  const onSearch = async (filtersParam = filters) => {
    const currentFilters = filtersParam;
    const type = currentFilters.docType;

    if (
      currentFilters.issueDateFrom &&
      currentFilters.issueDateTo &&
      currentFilters.issueDateFrom > currentFilters.issueDateTo
    ) {
      setError(t("filters.dateRangeError"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      if (type === "nfe") {
        setRowsNfe([]);

        const data = await fetchNfeHeaders({
          accessKey: currentFilters.accessKey,
          process: currentFilters.process,
          step: currentFilters.step,
          issueDateFrom: currentFilters.issueDateFrom,
          issueDateTo: currentFilters.issueDateTo,
          receivedDateFrom: currentFilters.receivedDateFrom,
          receivedDateTo: currentFilters.receivedDateTo,
          supplier_cnpj: currentFilters.supplier_cnpj,
          plant: currentFilters.plant,
        });

        localStorage.setItem(
          FILTER_STORAGE_KEY,
          JSON.stringify(currentFilters),
        );

        setRowsNfe(Array.isArray(data) ? data : []);
      } else if (type === "cte") {
        setRowsCte([]);

        const data = await fetchCteHeaders({
          accessKey: currentFilters.accessKey,
          issueDateFrom: currentFilters.issueDateFrom,
          issueDateTo: currentFilters.issueDateTo,
          receivedDateFrom: currentFilters.receivedDateFrom,
          receivedDateTo: currentFilters.receivedDateTo,
          supplier_cnpj: currentFilters.supplier_cnpj,
        });

        localStorage.setItem(
          FILTER_STORAGE_KEY,
          JSON.stringify(currentFilters),
        );

        setRowsCte(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Erro na busca:", err);
      setError(translateBackendMessage(err.message, isEnglish) || t("filters.fetchError"));
      setRowsNfe([]);
      setRowsCte([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(nextFilters));
  };

  useEffect(() => {
    onSearch(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstDocTypeRender = useRef(true);
  useEffect(() => {
    if (isFirstDocTypeRender.current) {
      isFirstDocTypeRender.current = false;
      return;
    }
    onSearch(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.docType]);

  return (
    <Box
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      <InboundFilter
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onSearch={() => onSearch(filters)}
        loading={loading}
        error={error}
      />

      <TableNfe
        docType={filters.docType}
        rows={filters.docType === "cte" ? rowsCte : rowsNfe}
        onRefresh={() => onSearch(filters)}
      />
    </Box>
  );
};

export default InboundListPage;